import { AnimatedWarningModal, WarningModal } from "@/common/modal/userCancelActionWarning";
import Modal, { ModalType } from "@/common/ui/Modal";
import { useModal } from "@/hooks/useModal";
import { dashboardSelector } from "@/store/features/dashboardSlice";
import React, { forwardRef, useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";

export interface ModalConfig {
  [key: string]: {
    component: React.ComponentType<any>;
    title: string;
    modalClassName?: string;
    type?: ModalType;
    showCloseIcon?: boolean;
    showCloseWarning?: boolean;
    asChild?: boolean;
    SideComponent?: () => JSX.Element;
    isQueryRequired?: boolean;
  };
}

export const withModal = (WrappedComponent: React.ComponentType<any>, config: ModalConfig) => {
  return forwardRef((props: any, ref) => {
    const { activeModal, modalProps, openModal, closeModal } = useModal(config);
    const [showWarning, setShowWarning] = useState(false);
    const { action } = useSelector(dashboardSelector);
    const warningResolveRef = useRef<((value: boolean) => void) | null>(null);

    const showWarningModal = useCallback((): Promise<boolean> => {
      return new Promise((resolve) => {
        warningResolveRef.current = resolve;
        setShowWarning(true);
      });
    }, []);

    const handleWarningResponse = useCallback((shouldClose: boolean) => {
      setShowWarning(false);
      if (warningResolveRef.current) {
        warningResolveRef.current(shouldClose);
        warningResolveRef.current = null;
      }
    }, []);

    const handleCloseModal = useCallback(
      async (props?: any) => {
        if (activeModal) {
          const modalConfig = config[activeModal];
          if (modalConfig?.showCloseWarning && action && action === "FORM_FILL" && !props?.disabledWarning) {
            const shouldClose = await showWarningModal();
            if (shouldClose) {
              if (modalProps?.closeModal) {
                modalProps.closeModal();
              } else {
                closeModal();
              }
            }
          } else {
            if (modalProps?.closeModal) {
              modalProps.closeModal();
            } else {
              closeModal();
            }
          }
        }
      },
      [activeModal, action]
    );

    const renderActiveModal = () => {
      if (activeModal && config[activeModal]) {
        const { component: ModalComponent, title, modalClassName = "", SideComponent, type, showCloseIcon, asChild = false } = config[activeModal];
        if (asChild) return <ModalComponent {...modalProps} onClose={closeModal} />;
        return (
          <Modal
            title={title}
            onClose={(props: any) => handleCloseModal(props)}
            className={modalClassName}
            forceTitle={modalProps?.forceTitle}
            type={type}
            showCloseIcon={showCloseIcon}
            SideComponent={SideComponent}
          >
            <ModalComponent {...modalProps} onClose={handleCloseModal} />
          </Modal>
        );
      }
      return null;
    };
    return (
      <>
        <WrappedComponent {...props} ref={ref} openModal={openModal} closeModal={closeModal} activeModal={activeModal} />
        {renderActiveModal()}
        {showWarning && (
          <AnimatedWarningModal>
            <WarningModal handleCancel={() => handleWarningResponse(false)} handleConfirm={() => handleWarningResponse(true)} />
          </AnimatedWarningModal>
        )}
      </>
    );
  });
};
